// Finance → Commissions under the compensation plans (CEO 2026-10-15).
// Shared types, the plan-status switch and plain-words formatters for the
// new Commissions screens. The API is apps/api/src/routes/comp-plans.ts —
// every number shown is the server's own; nothing here derives a commission.
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export type SplitPreset = "100/0" | "75/25" | "50/50";
export const SPLIT_PRESET_ORDER: SplitPreset[] = ["100/0", "75/25", "50/50"];

export interface PlanStatus {
  ready: boolean;
  splitPresets: Record<SplitPreset, string>;
}

/** Is the plan model switched on? (false until the one database step runs) */
export function usePlanStatus(enabled = true) {
  return useQuery({
    queryKey: ["commissions", "plan-status"],
    queryFn: () => api<PlanStatus>("/commissions/plan-status"),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

export function isFinanceRole(roles: string[] | undefined | null): boolean {
  return !!roles && (roles.includes("admin") || roles.includes("finance"));
}

// ─── Response shapes ─────────────────────────────────────────────────────

export type LineStatus = "estimated" | "eligible" | "approved_for_payout" | "paid" | "voided";

export interface CommissionLine {
  id: string;
  invoice_id: string;
  order_id: string;
  order_number: number | string;
  invoice_number: number | string | null;
  project_id: string;
  opportunity: string | null;
  client_name: string | null;
  rep_id: string;
  rep_name: string | null;
  rep_role: string;
  gross_profit: string | null;
  pool: string | null;
  rate: string | null;
  share_percent: string | null;
  amount: string | null;
  status: LineStatus;
  needs_review: string | null;
  eligible_at: string | null;
  paid_at: string | null;
  payout_batch_id: string | null;
  batch_period_start: string | null;
  plan_name: string | null;
  plan_version: number | null;
  revenue: string | null;
  balance_due: string | null;
  invoice_paid_at: string | null;
  order_stage: string | null;
  costs_finalized_at: string | null;
  /** Plain reasons the line isn't eligible yet ("Unpaid balance", …). */
  waiting_on?: string[] | null;
  /** Plain sentence when the line needs a Finance decision, else null. */
  reviewText?: string | null;
}

export interface Allocation {
  orderId: string;
  clientRepId: string;
  orderRepId: string | null;
  preset: SplitPreset;
  clientShareBp: number;
  poolPlanId: string | null;
  note: string | null;
  approvedBy: string;
  approvedAt: string;
}

export interface OrderDetail {
  order: {
    id: string;
    order_number: number | string;
    order_stage: string;
    costs_finalized_at: string | null;
    costs_finalized_by_name: string | null;
    project_id: string;
    opportunity: string | null;
    client_name: string | null;
    client_rep_id: string | null;
    client_rep_name: string | null;
    order_rep_id: string | null;
    order_rep_name: string | null;
    invoice_id: string | null;
    invoice_number: number | string | null;
    revenue: string | null;
    invoice_total: string | null;
    balance_due: string | null;
    paid_at: string | null;
  };
  margins: { revenue: string; bookedGrossProfit: string; billedGrossProfit: string; billedCost: string };
  eligibility: {
    eligible: boolean;
    eligibleAt: string | null;
    reasons: string[];
    reasonText: string[];
    invoiceId: string | null;
  } | null;
  allocation: Allocation | null;
  commissions: CommissionLine[];
  adjustments: Array<{
    id: string;
    rep_id: string;
    rep_name: string | null;
    amount: string;
    source: string;
    reason: string | null;
    status: string;
    created_at: string;
    decided_at: string | null;
    payout_batch_id: string | null;
  }>;
  history: Array<{ event_type: string; payload: unknown; occurred_at: string; actor_name: string | null }>;
}

export interface PayoutPeriod {
  start: string;
  end: string; // exclusive
}

export interface PayoutCalendar {
  anchor: string | null;
  current: PayoutPeriod | null;
  previous: PayoutPeriod | null;
}

export interface PayoutBatch {
  id: string;
  period_start: string;
  period_end: string;
  status: "draft" | "approved" | "paid" | string;
  approved_at: string | null;
  paid_on: string | null;
  payment_reference: string | null;
  approved_by_name: string | null;
  paid_recorded_by_name: string | null;
  commission_total: string;
  bonus_total: string;
  adjustment_total: string;
}

export interface BatchItem {
  kind: "commission" | "bonus" | "adjustment";
  id: string;
  rep_id: string;
  rep_name: string | null;
  amount: string;
  dated: string | null;
  ref: string | null;
  detail: string | null;
}

/** Not payable yet, so not in any batch (needs review / held negative). */
export interface WaitingItem {
  kind: "commission" | "adjustment" | string;
  id: string;
  rep_name: string | null;
  amount: string;
  ref: string | null;
  reason: string | null;
  reasonText: string;
}

export interface BatchDetailResponse {
  batch: PayoutBatch;
  items: BatchItem[];
  /** Left out of THIS batch by Finance — carries forward; can be added back. */
  leftOut: BatchItem[];
  waiting: WaitingItem[];
}

export interface CompSettingKeyInfo {
  key: string;
  label: string;
  blocks: string;
}

export interface Overview {
  totals: {
    estimated: string | null;
    eligible: string | null;
    approved: string | null;
    paid_ytd: string | null;
    needs_review: number | string | null;
  };
  calendar: PayoutCalendar;
  currentBatch: PayoutBatch | null;
  missingSettings: CompSettingKeyInfo[];
  pendingAdjustments: number;
  pendingBonuses: number;
  approvedBonusesUnpaid: string;
  promotionFlags: PromotionFlag[];
}

export interface PromotionFlag {
  repId: string;
  repName: string;
  onPlanSince: string;
  trailing6: string;
  trailing12: string;
  annualized: string;
  flagged: boolean;
}

export interface BonusTier {
  period: "monthly" | "quarterly" | "annual";
  threshold: number;
  amount: number;
}

export interface PlanRow {
  id: string;
  key: "account_manager" | "concierge" | "account_director" | string;
  name: string;
}

export interface PlanVersionRow {
  id: string;
  plan_id: string;
  version: number;
  effective_from: string;
  configured: boolean;
  commission_rate: string | number | null;
  base_salary_annual: string | number | null;
  revenue_bonus_tiers: BonusTier[] | null;
  development_bonus_annual_cap: string | number | null;
  notes: string | null;
  created_at: string;
}

export interface AssignmentRow {
  id: string;
  user_id: string;
  full_name: string;
  plan_id: string;
  effective_from: string;
  effective_to: string | null;
  reason: string | null;
  created_at: string;
}

export interface CompRep {
  id: string;
  full_name: string;
  title: string | null;
  roles: string[] | null;
  current_plan: string | null;
}

export interface PlansResponse {
  plans: PlanRow[];
  versions: PlanVersionRow[];
  assignments: AssignmentRow[];
  reps: CompRep[];
  promotionFlags: PromotionFlag[];
}

/** Plans, versions, assignments, reps — used by several tabs (one cache). */
export function usePlans(enabled = true) {
  return useQuery({
    queryKey: ["commissions", "plans"],
    queryFn: () => api<PlansResponse>("/commissions/plans"),
    enabled,
  });
}

// ─── Plain words ─────────────────────────────────────────────────────────

export const LINE_STATUS_LABEL: Record<string, string> = {
  estimated: "Estimated",
  eligible: "Eligible",
  approved_for_payout: "Approved for payout",
  paid: "Paid",
  voided: "Voided",
};

export function lineStatusTone(s: string): "active" | "prospect" | "warn" | "closed" | "info" {
  if (s === "paid") return "active";
  if (s === "approved_for_payout") return "active";
  if (s === "eligible") return "prospect";
  if (s === "voided") return "closed";
  return "info";
}

export const REVIEW_REASON_TEXT: Record<string, string> = {
  no_plan: "The Client Rep has no compensation plan on the order's date — assign one in Plans & reps.",
  plan_not_configured:
    "The plan that applies isn't configured yet (for example Account Director) — configure it, or choose another plan for this order.",
  mixed_plan: "The two reps are on plans whose mix isn't defined — choose which plan sets the pool.",
  plan_changed: "The Client Rep's plan changed between the order and its eligibility — confirm which plan applies.",
};

export function reviewText(code: string | null | undefined): string {
  if (!code) return "";
  return REVIEW_REASON_TEXT[code] ?? "Needs Finance review.";
}

export function roleLabel(r: string | null | undefined): string {
  if (r === "client_rep") return "Client Rep";
  if (r === "order_rep") return "Order Rep";
  return (r ?? "").replace(/_/g, " ");
}

export const BATCH_STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  approved: "Approved — not paid yet",
  paid: "Paid",
};

export const ADJ_SOURCE_LABEL: Record<string, string> = {
  manual: "Entered by Finance",
  credit_memo: "Client credit",
  late_cost: "Late cost",
  invoice_void: "Invoice voided",
};

export const BONUS_KIND_LABEL: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
  development: "Development",
};

const EVENT_LABEL: Record<string, string> = {
  "commission.estimated": "Commission estimated",
  "commission.earned": "Became eligible",
  "commission.earned_recomputed": "Eligible amount recalculated",
  "commission.allocation_set": "Split approved",
  "commission.recalculated": "Commission recalculated",
  "commission.paid": "Paid",
  "order.costs_finalized": "Costs finalized",
  "order.costs_reopened": "Costs reopened",
};

export function eventLabel(e: string): string {
  return EVENT_LABEL[e] ?? e.replace(/^[a-z]+\./, "").replace(/[._]/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** One extra plain-words detail from an audit payload, when there is one. */
export function eventDetail(e: string, payload: unknown): string | null {
  const p = (payload ?? {}) as Record<string, unknown>;
  if (e === "order.costs_reopened" && typeof p.reason === "string") return p.reason;
  if (e === "commission.allocation_set") {
    const after = (p.after ?? {}) as Record<string, unknown>;
    const bits = [typeof after.preset === "string" ? `Split ${after.preset}` : null, typeof after.note === "string" ? after.note : null];
    return bits.filter(Boolean).join(" — ") || null;
  }
  if ((e === "commission.estimated" || e === "commission.earned") && typeof p.amount === "string") {
    return `${roleLabel(typeof p.repRole === "string" ? p.repRole : null)} ${money(p.amount)}`.trim();
  }
  if (e === "commission.paid" && typeof p.paidOn === "string") return `Paid on ${fmtDay(p.paidOn)}`;
  return null;
}

// ─── Formatters ──────────────────────────────────────────────────────────

/** A person's name, or "Unassigned" when there isn't one. */
export function nameOr(n: string | null | undefined): string {
  return n && n.trim() ? n : "Unassigned";
}

/** Bonus period keys in words: "2026-Q3" → "Q3 2026", "2026-10" → "October 2026". */
export function periodKeyLabel(key: string | null | undefined): string {
  if (!key) return "—";
  const q = /^(\d{4})-Q([1-4])$/.exec(key);
  if (q) return `Q${q[2]} ${q[1]}`;
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }
  return key;
}

export function money(v: string | number | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** A fraction (0.08) → "8%". */
export function ratePct(v: string | number | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Number((n * 100).toFixed(2))}%`;
}

/** A date-only "2026-10-12" (no timezone shift) or a timestamp → "Oct 12, 2026". */
export function fmtDay(s: string | null | undefined): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Add days to a "YYYY-MM-DD" (calendar arithmetic, no timezone drift). */
export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! + days));
  return dt.toISOString().slice(0, 10);
}

/** A payout period [start, end) as people read it: "Oct 12 – Oct 25, 2026". */
export function periodLabel(start: string, endExclusive: string): string {
  return `${fmtDay(start)} – ${fmtDay(addDays(endExclusive, -1))}`;
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The server's reason when it gave one (409 rule errors carry `reason`). */
export function errReason(e: unknown): string {
  if (e instanceof ApiError) {
    const b = (e.body ?? {}) as { reason?: unknown; error?: unknown };
    if (typeof b.reason === "string") return b.reason;
    if (e.status === 400) return "Some fields are missing or not in the right format — check them and try again.";
    if (e.status === 403) return "You don't have access to this.";
    if (typeof b.error === "string" && b.error !== "failed") return b.error;
    return "Something went wrong — try again.";
  }
  return e instanceof Error ? e.message : "Something went wrong — try again.";
}
